import { ColourPicker } from '@components/core-ui/ColourPicker';

import { Input } from '@components/core-ui/form-elements/Input';
import { Switch } from '@components/core-ui/form-elements/Switch';
import { EntityIconMd } from '@components/core-ui/EntityIcon/variations/EntityIconMd';
import QuestionIcon from '@components/icons/QuestionIcon18.svg';
import { FindOneTeamResponseDto, UpdateTeamBodyDto } from 'app/API';
import { useState } from 'react';
import { InputBlockWrapper } from './styles/InputBlockWrapper';
import { InputWrapper } from './styles/InputWrapper';
import { LeftSide } from './styles/LeftSide';
import { PermissionLabel } from './styles/PermissionLabel';
import { QuestionContent } from './styles/QuestionContent';
import { QuestionIconWrapper } from './styles/QuestionIconWrapper';
import { RightSide } from './styles/RightSide';
import { RightSideColumns } from './styles/RightSideColumns';
import { Select } from '@components/core-ui/form-elements/Select';
import { Wrapper } from './styles/Wrapper';

const toggleOrgPermissionsGroups: (keyof UpdateTeamBodyDto)[][] = [
  ['manageOrganization', 'manageWorkspaces', 'manageMembers', 'managePermissions'],

  ['manageBilling', 'viewBilling', 'manageLicenses', 'viewLicenses'],

  ['viewReports', 'editJobs', 'submitJobs', 'reassignJobs', 'deleteJobs'],

  ['editRepositories', 'viewRepositories', 'viewJobCost'],
];

const toggleWSPermissionsGroups: (keyof UpdateTeamBodyDto)[][] = [
  ['editJobTranscript', 'proofreadJobTranscript'],
  ['finalizeJobTranscript', 'markJobComplete'],
];

const camelCaseToTitleCase = (text: string) => {
  const result = text.replace(/([A-Z])/g, ' $1').replace('Repositories', 'Cases');
  return result.charAt(0).toUpperCase() + result.slice(1);
};

const getInitialValues = (team: FindOneTeamResponseDto): UpdateTeamBodyDto => {
  return {
    name: team.name,
    slug: team.slug,
    colour: team.colour,

    viewRepositories: team.viewRepositories,
    editRepositories: team.editRepositories,

    viewBilling: team.viewBilling,
    manageBilling: team.manageBilling,

    viewLicenses: team.viewLicenses,
    manageLicenses: team.manageLicenses,

    viewJobs: team.viewJobs as UpdateTeamBodyDto['viewJobs'],
    editJobs: team.editJobs,
    reassignJobs: team.reassignJobs,
    deleteJobs: team.deleteJobs,
    submitJobs: team.submitJobs,
    viewJobCost: team.viewJobCost,

    editJobTranscript: team.editJobTranscript,
    proofreadJobTranscript: team.proofreadJobTranscript,
    finalizeJobTranscript: team.finalizeJobTranscript,
    markJobComplete: team.markJobComplete,

    viewReports: team.viewReports,

    manageMembers: team.manageMembers,
    managePermissions: team.managePermissions,
    manageWorkspaces: team.manageWorkspaces,

    manageOrganization: team.manageOrganization,
  };
};

type TProps = {
  team: FindOneTeamResponseDto;
};

export const TeamInformation = ({ team }: TProps) => {
  const [form, setForm] = useState<UpdateTeamBodyDto>(getInitialValues(team));

  return (
    <>
      <Wrapper>
        <LeftSide>
          <h3>General</h3>
        </LeftSide>

        <RightSide>
          <RightSideColumns>
            <EntityIconMd
              name={form.name || form.slug || ''}
              backgroundColor={form.colour}
              color="var(--color-surface)"
            />
            <div>
              <ColourPicker colour={form.colour} setColour={(colour) => setForm({ ...form, colour })} />
              <p className="text-xs">This icon color will be used to identify your team.</p>
            </div>
          </RightSideColumns>
          <RightSideColumns>
            <div className="team-name">
              <InputWrapper>
                <label htmlFor="name">Team name</label>
                <Input
                  className="form-input w-full"
                  disabled={true}
                  onChange={() => {}}
                  name="name"
                  value={form.name}
                  label="Team name"
                />
              </InputWrapper>
              <InputWrapper>
                <label htmlFor="slug">Team URL</label>
                <Input
                  className="form-input w-full"
                  disabled={true}
                  onChange={() => {}}
                  name="slug"
                  value={form.slug}
                  label="Team URL"
                />
              </InputWrapper>
            </div>
          </RightSideColumns>
        </RightSide>
      </Wrapper>
      <Wrapper>
        <LeftSide>
          <h3>Permissions</h3>
        </LeftSide>
        <RightSide>
          <RightSideColumns>
            <div className="view-jobs">
              <InputBlockWrapper className="view-jobs-field">
                <InputWrapper>
                  <label htmlFor="viewJobs">View jobs</label>
                  <Select
                    name="viewJobs"
                    className="form-select min-w-0 w-full"
                    disabled={true}
                    onChange={() => {}}
                    label=""
                    value={form.viewJobs}
                    options={[
                      { value: 'assigned', label: 'Assigned' },
                      { value: 'assignedAndUnassigned', label: 'Assigned & Unassigned' },
                      { value: 'all', label: 'All' },
                      { value: 'none', label: 'None' },
                    ]}
                  />
                </InputWrapper>
              </InputBlockWrapper>
              <div className="view-jobs-hint">
                <QuestionIconWrapper className="absolute left-0 top-9">
                  <QuestionIcon />
                  <QuestionContent className="question-content">
                    Choose the type of job list that fits this team&apos;s needs:
                    <ul>
                      <li>All - view all jobs within an attached Workspace</li>
                      <li>Assigned - only view jobs which have been assigned to the current user</li>
                      <li>
                        Assigned and Unassigned - view jobs which have been assigned to the current user and jobs which
                        are available to be claimed (with the Edit Job Transcript or Proofread Job Transcript
                        Permissions)
                      </li>
                      <li>None - disable the job list.</li>
                    </ul>
                  </QuestionContent>
                </QuestionIconWrapper>
              </div>
            </div>
          </RightSideColumns>

          <PermissionLabel>Organization level</PermissionLabel>
          <div className="flex mt-4 gap-16">
            {toggleOrgPermissionsGroups.map((group, i) => (
              <div key={i} className="mr-4 min-w-12">
                {group.map((permission, ii) => (
                  <div key={`${i}-${ii}`} className="flex mb-2">
                    <label htmlFor={permission} className="mr-4 whitespace-nowrap">
                      {camelCaseToTitleCase(permission)}
                    </label>
                    <div className="flex-grow grid justify-items-end">
                      <Switch checked={form[permission] as boolean} disabled={true} onChange={() => {}} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <PermissionLabel>Workspace level</PermissionLabel>
          <div className="flex mt-4 gap-16">
            {toggleWSPermissionsGroups.map((group, i) => (
              <div key={i} className="mr-4 min-w-12">
                {group.map((permission, ii) => (
                  <div key={`${i}-${ii}`} className="flex mb-2">
                    <label htmlFor={permission} className="mr-4 whitespace-nowrap">
                      {camelCaseToTitleCase(permission)}
                    </label>
                    <div className="flex-grow grid justify-items-end">
                      <Switch checked={form[permission] as boolean} disabled={true} onChange={() => {}} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </RightSide>
      </Wrapper>
    </>
  );
};
